import {
  useMutation,
  useQueryClient,
  QueryKey,
  UseMutationResult,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import { useTranslation } from '../../i18n';

interface CreateMutationOpts<TData, TVariables> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  invalidateKeys:
    | QueryKey[]
    | ((variables: TVariables, data: TData) => QueryKey[]);
  successMessage: string;
  errorMessage: string;
  onSuccessExtra?: (data: TData, variables: TVariables) => void;
  delayedInvalidateKeys?: QueryKey[];
}

export function createMutationHook<TData, TVariables>(
  opts: CreateMutationOpts<TData, TVariables>,
): () => UseMutationResult<TData, Error, TVariables> {
  return () => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
      mutationFn: opts.mutationFn,
      onSuccess: (data, variables) => {
        const keys =
          typeof opts.invalidateKeys === 'function'
            ? opts.invalidateKeys(variables, data)
            : opts.invalidateKeys;

        for (const key of keys) {
          queryClient.invalidateQueries({ queryKey: key });
        }

        if (opts.delayedInvalidateKeys) {
          setTimeout(() => {
            for (const key of opts.delayedInvalidateKeys!) {
              queryClient.invalidateQueries({ queryKey: key });
            }
          }, 2500);
        }

        opts.onSuccessExtra?.(data, variables);
        toast.success(t(opts.successMessage));
      },
      onError: (error: Error) => {
        toast.error(error.message || t(opts.errorMessage));
      },
    });
  };
}
